import { Component, AfterViewInit, ElementRef, HostListener, OnInit, ViewChild, OnDestroy, Renderer2, Inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, map, mergeMap } from 'rxjs';
import { DOCUMENT, CommonModule } from '@angular/common';

@Component({
  selector: 'app-agentic-ai-in-sdlc',
  templateUrl: './agentic-ai-in-sdlc.component.html',
  styleUrls: ['./agentic-ai-in-sdlc.component.scss']
})
export class AgenticAiInSdlcComponent implements OnInit, AfterViewInit, OnDestroy {
  constructor(private router: Router, private activatedRoute: ActivatedRoute,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private _document: Document
  ) { }
  private jsonLdScript: any;
  ngOnInit(): void {
    this.injectSchemaMarkup();
    this.router.events.pipe(
      filter((event: any) => event instanceof NavigationEnd),
      map(() => this.activatedRoute),
      map((route) => {
        while (route.firstChild) route = route.firstChild;
        return route;
      }),
      filter((route) => route.outlet === 'primary'),
      mergeMap((route: { data: any; }) => route.data)
    )
  }

  injectSchemaMarkup(): void {
    const fullGraphSchema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://www.kalpitatechnologies.com/tech-blogs/agentic-ai-in-sdlc#webpage",
          "url": "https://www.kalpitatechnologies.com/tech-blogs/agentic-ai-in-sdlc",
          "name": "Agentic AI in SDLC: How It Transforms Full-Stack Development in Multi-Cloud Ecosystems | Kalpita Technologies",
          "description": "In-depth guide on how agentic AI transforms the Software Development Lifecycle by embedding autonomous agents across requirements, design, development, testing, deployment, and maintenance.",
          "isPartOf": { "@id": "https://www.kalpitatechnologies.com/#website" },
          "inLanguage": "en"
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://www.kalpitatechnologies.com/tech-blogs/agentic-ai-in-sdlc#breadcrumb",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.kalpitatechnologies.com" },
            { "@type": "ListItem", "position": 2, "name": "Blog Index", "item": "https://www.kalpitatechnologies.com/tech-blogs/blog" },
            { "@type": "ListItem", "position": 3, "name": "Agentic AI in SDLC", "item": "https://www.kalpitatechnologies.com/tech-blogs/agentic-ai-in-sdlc" }
          ]
        },
        {
          "@type": "BlogPosting",
          "@id": "https://www.kalpitatechnologies.com/tech-blogs/agentic-ai-in-sdlc#article",
          "headline": "Agentic AI in SDLC: How It Transforms Full-Stack Development in Multi-Cloud Ecosystems",
          "articleBody": "Agentic AI is reshaping the Software Development Lifecycle (SDLC) by embedding autonomous, collaborative agents across every phase...",
          "author": [
            { "@type": "Person", "name": "Bhavani" },
            { "@type": "Person", "name": "Dinakar" }
          ],
          "publisher": {
            "@type": "Organization",
            "name": "Kalpita Technologies",
            "@id": "https://www.kalpitatechnologies.com/#organization",
            "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png" }
          },
          "datePublished": "2025-09-26",
          "image": "https://www.kalpitatechnologies.com/assets/resources/blog/agentic-ai-in-sdlc/sdlc-banner.webp"
        },
        {
          "@type": "FAQPage",
          "@id": "https://www.kalpitatechnologies.com/tech-blogs/agentic-ai-in-sdlc#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What is agentic AI in the context of SDLC?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Agentic AI in SDLC refers to autonomous, goal-directed AI agents that manage, execute, and optimize tasks across the Software Development Lifecycle."
              }
            }
          ]
        },
        {
          "@type": "Organization",
          "@id": "https://www.kalpitatechnologies.com/#organization",
          "name": "Kalpita Technologies",
          "url": "https://www.kalpitatechnologies.com/",
          "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png" }
        }
      ]
    };

    this.jsonLdScript = this.renderer.createElement('script');
    this.jsonLdScript.type = 'application/ld+json';
    this.jsonLdScript.text = JSON.stringify(fullGraphSchema);
    this.renderer.appendChild(this._document.head, this.jsonLdScript);
  }

  removeSchemaMarkup(): void {
    if (this.jsonLdScript) {
      this.renderer.removeChild(this._document.head, this.jsonLdScript);
      this.jsonLdScript = null;
    }
  }
  ngOnDestroy(): void {
    this.removeSchemaMarkup();
  }
  nav(url: any) {
    this.router.navigateByUrl(url)
  }

  @ViewChild('tableresponsiveImage', { static: false }) image!: ElementRef<HTMLImageElement>;

  originalWidth = 1920;
  originalHeight = 797;

  ngAfterViewInit(): void {
    this.resizeImageMap();
    this.image.nativeElement.onload = () => this.resizeImageMap();
  }

  @HostListener('window:resize')
  resizeImageMap(): void {
    const img = this.image.nativeElement;
    if (!img) return;

    const wRatio = img.clientWidth / this.originalWidth;
    const hRatio = img.clientHeight / this.originalHeight;

    document.querySelectorAll<HTMLAreaElement>('map[name="table-image-map"] area')
      .forEach(area => {
        const rawCoords =
          area.getAttribute('data-original-coords')
          || area.getAttribute('coords')
          || '';
        if (!rawCoords) return;

        area.setAttribute('data-original-coords', rawCoords);
        const scaled = rawCoords
          .split(',')
          .map((c, i) =>
            Math.round(
              Number(c) * (i % 2 === 0 ? wRatio : hRatio)
            )
          )
          .join(',');

        area.setAttribute('coords', scaled);
      });
  }

  scrollToSection(sectionId: string): void {
    const el = document.getElementById(sectionId);
    if (!el) return;
    const yOffset = 5 * 16;
    const top =
      el.getBoundingClientRect().top + window.pageYOffset - yOffset;
    window.scrollTo({ top, behavior: 'smooth' });
  }
}
